import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Single shared QueryClient for the whole app.
 *
 * Defaults chosen for a CMMS app where data freshness matters but
 * over-aggressive refetch wastes mobile bandwidth (techs on VSAT):
 *
 * - staleTime 30s: most lists stay valid for half a minute.
 * - retry 1 for queries: one bounce-back, then surface the error.
 * - retry 0 for mutations: never silently retry a write.
 * - refetchOnWindowFocus disabled: the user manually controls reloads.
 *
 * Auth errors (401/403) are NOT retried — useCurrentUser handles them
 * by routing to the login page.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            const code = error.statusCode ?? 0;
            if (code === 401 || code === 403 || code === 404) return false;
          }
          return failureCount < 1;
        },
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
